"use client";
"use no memo";

import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from "react";
import { useTheme } from "next-themes";
import { USER_SETTING_KEY_THEME, ThemeMode } from "@/lib/user-settings";
import type {User as FirebaseUser} from "firebase/auth";
import {deleteCookie, setCookie} from "cookies-next";
import {useRouter} from "next/navigation";
import {v4 as uuidv4} from 'uuid';

import {usePostHog} from "posthog-js/react";
import {APP_LOCAL_STORAGE_CLIENT_ID} from "@/lib/constants";
import {upsertUser, UserDTO} from "@/app/[locale]/(auth)/signin/_actions/user-server-actions";
import {CustomClaim} from "@/openapi/generated";
import {useAppParams} from "@/hooks/use-app-params";
import {useLogger} from "@/lib/use-logger";
import {ILogObj, Logger} from "tslog";
import {fetchExtensionCustomToken} from "@/app/actions/extension-token";

interface SelectedShopContextValue {
    id: string;
    name?: string;
}

interface IAppContext {
    user: UserDTO | null;
    isAdmin: boolean;
    loading: boolean;
    clientId: string | null;
    clientVersion: string | null;
    extensionInstalled: boolean | null;
    updateUser: (updates: Partial<UserDTO>) => Promise<void>;
    selectedShop: SelectedShopContextValue | null;
    setSelectedShop: (shop: SelectedShopContextValue | null) => void;
}

const AppContext = createContext<IAppContext>({
    user: null,
    isAdmin: false,
    loading: true,
    clientId: null,
    clientVersion: null,
    extensionInstalled: null,
    updateUser: async() => {
        return Promise.resolve();
    },
    selectedShop: null,
    setSelectedShop: () => {

    }
});

interface IAppProviderProps {
    children: ReactNode;
    initialUser?: FirebaseUser | null;
}

// Helper function to get or create a unique client ID and store it in localStorage
function getOrCreateClientId(log: Logger<ILogObj>): string {
    let clientId = localStorage.getItem(APP_LOCAL_STORAGE_CLIENT_ID);
    if (!clientId) {
        clientId = typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : uuidv4();

        localStorage.setItem(APP_LOCAL_STORAGE_CLIENT_ID, clientId);
        log.info("Unique Client-ID generated and stored:", clientId);
    } else {
        log.info("Existing Unique Client-ID loaded:", clientId);
    }
    return clientId;
}

function sendUserAuthInfo(user: UserDTO | null, firebaseIdToken?: string | null, customToken?: string | null) {
    if (user) {
        window.postMessage(
            {type: 'DONISTA_AUTH', user, firebaseIdToken: firebaseIdToken ?? null, customToken: customToken ?? null},
            window.location.origin
        );
    } else {
        window.postMessage(
            {type: 'DONISTA_AUTH', user: null, firebaseIdToken: null, customToken: null},
            window.location.origin
        );
    }
}

export function AppProvider({children}: IAppProviderProps) {
    const log = useLogger(import.meta.url);
    const router = useRouter();
    const posthog = usePostHog();
    const {referrerUserId} = useAppParams();
    const { setTheme } = useTheme();

    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [user, setUser] = useState<UserDTO | null>(null);
    const [loading, setLoading] = useState(true);

    const [clientId, setClientId] = useState<string>("");
    const [clientVersion, setClientVersion] = useState<string>("");

    const [extensionInstalled, setExtensionInstalled] = useState(false)

    const [selectedShop, setSelectedShop] = useState<SelectedShopContextValue | null>(null);
    const extensionTokenPromiseRef = useRef<Promise<string | null> | null>(null);
    const authInitDeferred = useRef(false);

    useEffect(() => {
        if (!document.cookie.includes('__session')) {
            /* eslint-disable react-hooks/set-state-in-effect -- one-time mount: read external cookie to skip auth loading for anonymous visitors */
            setLoading(false);
            /* eslint-enable react-hooks/set-state-in-effect */
        }
    }, []);

    const updateUser = async (updates: Partial<UserDTO>) => {
        // logger.log("updated user in app-context", updates);
        if (!user) return; // Early return if no user
        
        const newUser = {...user, ...updates};
        // logger.log("updated user in app-context: newUser:", newUser);
        
        setUser(newUser);
        sendUserAuthInfo(newUser);
        
        await upsertUser(newUser);
    };;

    /** Disable prefetch globally **/
    // eslint-disable-next-line react-hooks/immutability -- intentional: override prefetch to prevent unwanted route preloading
    useEffect(() => { router.prefetch = async () => {} }, [router])

    /** Get the current user (if a session-cookie exists) server-side. **/
    useEffect(() => {
        if (!document.cookie.includes('__session')) return;

        let cancelled = false;

        async function fetchCurrentUser() {
            const {getCurrentUser} = await import('@/contexts/_actions/app-context-actions');
            if (cancelled) return;

            let currentUser: Awaited<ReturnType<typeof getCurrentUser>>;
            try {
                currentUser = await getCurrentUser();
            } catch {
                currentUser = null;
            }

            // If the server returned null but the client still has a valid auth
            // session, the __session cookie likely held an expired token. Force-
            // refresh the token, update the cookie, then retry once.
            if (!currentUser) {
                const {getFirebaseAuth} = await import('@/lib/firebase/client-app');
                const clientUser = getFirebaseAuth().currentUser;
                if (clientUser) {
                    log.debug("fetchCurrentUser: stale token detected, force-refreshing");
                    const freshToken = await clientUser.getIdToken(true);
                    setCookie("__session", freshToken);
                    if (cancelled) return;
                    try {
                        const retried = await getCurrentUser();
                        setFirebaseUser(retried);
                    } catch {
                        setFirebaseUser(null);
                    }
                    return;
                }
            }

            if (!cancelled) {
                setFirebaseUser(currentUser);
            }
        }

        fetchCurrentUser();
        return () => {
            cancelled = true;
        };
    }, [log]);

    const userThemeSetting = user?.settings?.[USER_SETTING_KEY_THEME];
    useEffect(() => {
        const isValid = userThemeSetting === ThemeMode.Light || userThemeSetting === ThemeMode.Dark || userThemeSetting === ThemeMode.System;
        setTheme(isValid ? (userThemeSetting as ThemeMode) : ThemeMode.Light);
    }, [userThemeSetting, setTheme])

    /** Communicate with the browser-extension **/
    useEffect(() => {
        let stale = false;

        const handleMessage = async (event: MessageEvent) => {
            // Validate the origin.
            if (event.origin !== window.location.origin) return;

            // When the DONISTA_EXTENSION_READY message is received...
            if (event.data && event.data.type === 'DONISTA_EXTENSION_READY') {
                // Send an acknowledgment message back to the extension.
                window.postMessage({
                    type: 'DONISTA_WEBAPP_READY',
                    clientId: getOrCreateClientId(log)
                }, window.location.origin);

                const {getFirebaseAuth} = await import('@/lib/firebase/client-app');
                const authUser = getFirebaseAuth().currentUser;
                const token = authUser ? await authUser.getIdToken() : null;
                if (stale) return;

                let customToken: string | null = null;
                if (user && token) {
                    if (!extensionTokenPromiseRef.current) {
                        extensionTokenPromiseRef.current = fetchExtensionCustomToken();
                    }
                    customToken = await extensionTokenPromiseRef.current;
                    if (stale) return;
                }

                if (user) {
                    sendUserAuthInfo(user, token, customToken);
                }
                setExtensionInstalled(true)

                if (user) {
                    posthog.identify(user.id, {extensionInstalled: true});
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            stale = true;
            extensionTokenPromiseRef.current = null;
            window.removeEventListener('message', handleMessage);
        };
    }, [extensionInstalled, firebaseUser, log, posthog, user])

    /** Maintain/Synchronize the client-side auth status with the '__session' cookie **/
    useEffect(() => {
        /* eslint-disable react-hooks/set-state-in-effect -- one-time init alongside token listener setup */
        setClientId(getOrCreateClientId(log));
        setClientVersion(process.env.NEXT_PUBLIC_BUILD_ID!);
        /* eslint-enable react-hooks/set-state-in-effect */
        const stale = { current: false };
        let unsubscribeToken: (() => void) | undefined;
        let rafId: number | undefined;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        async function initTokenListener() {
            const {onIdTokenChanged} = await import('@/app/[locale]/(auth)/signin/auth');
            if (stale.current) return;
            unsubscribeToken = onIdTokenChanged(async (authUser: FirebaseUser | null) => {
                log.debug("onIdTokenChanged");

                // If the current user does not match the token user, reload the page
                if (firebaseUser != null && firebaseUser?.uid !== authUser?.uid) {
                    window.location.reload();
                }

                let idToken: string | null = null;
                if (authUser) {
                    idToken = await authUser.getIdToken();
                    setCookie("__session", idToken);
                } else {
                    deleteCookie("__session");
                    sendUserAuthInfo(null);
                }

                setFirebaseUser(authUser);

                if (authUser) {
                    const {upsertFirebaseUser} = await import('@/app/[locale]/(auth)/signin/_actions/user-server-actions');
                    const user = await upsertFirebaseUser(authUser.toJSON() as FirebaseUser, referrerUserId);
                    const myClientId = getOrCreateClientId(log);
                    const myClientVersion = process.env.NEXT_PUBLIC_BUILD_ID!;
                    posthog.identify(
                        user.id,
                        {
                            email: user.personalDetails?.email,
                            name: user.personalDetails?.firstName + " " + user.personalDetails?.lastName,
                            firebaseUid: user.firebaseUid,
                            clientId: myClientId,
                            clientVersion: myClientVersion
                        }
                    );

                    log.debug('PostHog Distinct ID:', posthog.get_distinct_id());
                    setUser(user);

                    if (!extensionTokenPromiseRef.current) {
                        extensionTokenPromiseRef.current = fetchExtensionCustomToken();
                    }
                    const customToken = await extensionTokenPromiseRef.current;
                    sendUserAuthInfo(user, idToken, customToken);
                }
                setLoading(false);
            });
        }

        const hasSession = document.cookie.includes('__session');
        if (hasSession || authInitDeferred.current) {
            initTokenListener();
        } else {
            authInitDeferred.current = true;
            rafId = requestAnimationFrame(() => {
                timeoutId = setTimeout(() => initTokenListener(), 0);
            });
        }

        return () => {
            stale.current = true;
            if (rafId !== undefined) cancelAnimationFrame(rafId);
            if (timeoutId !== undefined) clearTimeout(timeoutId);
            unsubscribeToken?.();
        };
    }, [firebaseUser, posthog, referrerUserId, log]);


    useEffect(() => {
        log.debug(' PostHog Distinct ID:', posthog.get_distinct_id());
    }, [posthog, log]);

    const isAdmin = user?.customClaims?.some(c => c === CustomClaim.ROLE_ADMIN) ?? false;

    return (
        <AppContext.Provider value={{
            user,
            isAdmin,
            loading,
            clientId,
            clientVersion,
            extensionInstalled,
            updateUser,
            selectedShop,
            setSelectedShop,
        }}>
            {children}
        </AppContext.Provider>
    );
}

export function useAppContext(): IAppContext {
    return useContext(AppContext);
}
